import { CANCELLATION_REASONS } from "./orderEditing";
import { buildItemOptionChips } from "./saleItemPresentation";

/**
 * How Orders are shown to staff: history labels, state cards, option chips and
 * small list helpers. Presentation only - every business decision (actions,
 * totals, stock) still comes from the server response.
 */

const fill = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, String(value)),
    String(template),
  );

const upper = (value) => String(value || "").trim().toUpperCase();
const lower = (value) => String(value || "").trim().toLowerCase();

const CHANNEL_LABEL_KEYS = {
  WEBSHOP: "storefrontOrders.channelWebshop",
  INSTAGRAM: "storefrontOrders.channelInstagram",
  FACEBOOK: "storefrontOrders.channelFacebook",
  PHONE: "storefrontOrders.channelPhone",
  OTHER: "storefrontOrders.channelOther",
};

/** Every real `OrderEventType` from the schema. Raw enum names never reach the UI. */
export const ORDER_EVENT_PRESENTATION = {
  ORDER_CREATED: { labelKey: "ordersUi.eventCreated", tone: "info" },
  ORDER_EDITED: { labelKey: "ordersUi.eventEdited", tone: "neutral" },
  ORDER_CONFIRMED: { labelKey: "ordersUi.eventConfirmed", tone: "ok" },
  ORDER_REOPENED: { labelKey: "ordersUi.eventReopened", tone: "warn" },
  ORDER_CANCELLED: { labelKey: "ordersUi.eventCancelled", tone: "danger" },
  SHIPMENT_REQUESTED: { labelKey: "ordersUi.eventShipmentRequested", tone: "info" },
  SHIPMENT_CREATED: { labelKey: "ordersUi.eventShipmentCreated", tone: "info" },
  SHIPMENT_FAILED: { labelKey: "ordersUi.eventShipmentFailed", tone: "danger" },
  ORDER_DELIVERED: { labelKey: "ordersUi.eventDelivered", tone: "ok" },
  RETURNED_TO_SENDER: { labelKey: "ordersUi.eventReturnedToSender", tone: "warn" },
  SALE_CREATED: { labelKey: "ordersUi.eventSaleCreated", tone: "ok" },
  SALE_FINALIZATION_FAILED: { labelKey: "ordersUi.eventSaleFailed", tone: "danger" },
};

const UNKNOWN_EVENT = { labelKey: "ordersUi.eventOther", tone: "neutral" };

export function resolveCancellationReasonLabelKey(reason) {
  const value = upper(reason);
  return CANCELLATION_REASONS.find((entry) => entry.value === value)?.labelKey || null;
}

const countPriceOverrides = (metadata) =>
  Array.isArray(metadata?.priceOverrides) ? metadata.priceOverrides.length : 0;

/**
 * A short, human description built only from facts the event actually recorded.
 * Technical payloads (reservation ids, provider errors) are never shown.
 */
function describeEventDetail(type, metadata, t, formatAmount) {
  switch (type) {
    case "ORDER_CREATED": {
      const channelKey = CHANNEL_LABEL_KEYS[upper(metadata.orderChannel)];
      const overrides = countPriceOverrides(metadata);
      return (
        [
          channelKey ? fill(t("ordersUi.eventCreatedFrom"), { source: t(channelKey) }) : null,
          overrides ? fill(t("ordersUi.eventPriceChangedCount"), { count: overrides }) : null,
        ]
          .filter(Boolean)
          .join(" · ") || null
      );
    }
    case "ORDER_EDITED": {
      const overrides = countPriceOverrides(metadata);
      return [
        metadata.replacedProducts ? t("ordersUi.eventEditedProducts") : t("ordersUi.eventEditedDetails"),
        overrides ? fill(t("ordersUi.eventPriceChangedCount"), { count: overrides }) : null,
        metadata.codAmount != null && formatAmount
          ? fill(t("ordersUi.eventCodAmount"), { amount: formatAmount(metadata.codAmount) })
          : null,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    case "ORDER_CONFIRMED":
      return t("ordersUi.eventConfirmedHint");
    case "ORDER_REOPENED":
      return t("ordersUi.eventReopenedHint");
    case "ORDER_CANCELLED": {
      const reasonKey = resolveCancellationReasonLabelKey(metadata.reason);
      return reasonKey ? fill(t("ordersUi.eventReason"), { reason: t(reasonKey) }) : null;
    }
    case "SHIPMENT_CREATED":
      return metadata.pod ? fill(t("ordersUi.eventPod"), { pod: metadata.pod }) : null;
    case "SHIPMENT_FAILED":
      return t("ordersUi.eventShipmentFailedHint");
    case "RETURNED_TO_SENDER":
      return metadata.note ? String(metadata.note) : t("ordersUi.eventReturnedHint");
    case "SALE_CREATED":
      return metadata.saleNumber ? fill(t("ordersUi.eventSaleNumber"), { number: metadata.saleNumber }) : null;
    case "SALE_FINALIZATION_FAILED":
      return t("ordersUi.eventSaleFailedHint");
    default:
      return null;
  }
}

export function describeOrderEvent(event, t, formatAmount) {
  const type = upper(event?.type);
  const presentation = ORDER_EVENT_PRESENTATION[type] || UNKNOWN_EVENT;
  const metadata = event?.metadata && typeof event.metadata === "object" ? event.metadata : {};

  return {
    id: event?.id,
    title: t(presentation.labelKey),
    description: describeEventDetail(type, metadata, t, formatAmount),
    tone: presentation.tone,
    createdAt: event?.createdAt || null,
  };
}

/**
 * The one info card at the top of the drawer: what matters operationally in the
 * order's current state.
 */
export function resolveOrderStateCard(order) {
  const status = lower(order?.orderStatus);
  const shipment = lower(order?.shipmentStatus);
  const reservation = lower(order?.inventoryReservation?.state);

  if (status === "cancelled") {
    return { tone: "danger", titleKey: "ordersUi.stateCancelled", hintKey: null };
  }
  if (status === "returned_to_sender") {
    return {
      tone: "warn",
      titleKey: "storefrontOrders.statusReturnedToSender",
      hintKey: "ordersUi.stateReturnedHint",
    };
  }
  if (status === "delivered") {
    return { tone: "ok", titleKey: "ordersUi.stateDelivered", hintKey: null };
  }
  if (status === "confirmed") {
    if (shipment === "failed") {
      return { tone: "warn", titleKey: "ordersUi.stateShipmentFailed", hintKey: "ordersUi.stateShipmentFailedHint" };
    }
    if (shipment === "created" || shipment === "requested") {
      return { tone: "info", titleKey: "ordersUi.stateInDelivery", hintKey: "ordersUi.stateInDeliveryHint" };
    }
    return { tone: "ok", titleKey: "ordersUi.stateConfirmed", hintKey: "ordersUi.stateConfirmedHint" };
  }
  if (reservation === "active") {
    return { tone: "ok", titleKey: "storefrontOrders.stockReserved", hintKey: "storefrontOrders.stockReservedHint" };
  }
  return { tone: "warn", titleKey: "ordersUi.stateCheckStock", hintKey: "ordersUi.stateCheckStockHint" };
}

/** "Ngjyra: White", "Nr. 42" chips for a saved order line (snapshot option summary). */
export function buildOrderLineChips(line, t) {
  return buildItemOptionChips(line?.optionSummary || line?.variantTitle, t);
}

/** Chips for a wizard search item / basket line, which carry colour and size fields. */
export function buildWizardItemChips(item, t) {
  if (item?.color || item?.size) {
    return [
      item.color ? t("commerceCommon.optionColorLabel").replace("{value}", item.color) : null,
      item.size ? t("commerceCommon.optionSizeLabel").replace("{value}", item.size) : null,
    ].filter(Boolean);
  }

  return buildItemOptionChips(item?.optionSummary || item?.variantTitle, t);
}

/**
 * The charged unit price, plus the original only when it really differs. Two
 * identical prices are never shown.
 */
export function buildLinePriceDisplay(line) {
  const final = Number(line?.finalUnitPriceAmount ?? line?.unitPriceAmount ?? 0);
  const original = Number(line?.originalUnitPriceAmount ?? final);

  return {
    final,
    original: line?.priceOverridden && original > final ? original : null,
  };
}

export function isOrderItemAddable(item) {
  return Boolean(item?.canAddManually) && item?.price != null && Number(item?.availableQuantity || 0) > 0;
}

/** Addable products first, then the rest; the server's order is kept inside each group. */
export function sortOrderSearchResults(items) {
  const list = Array.isArray(items) ? items : [];
  return [...list.filter(isOrderItemAddable), ...list.filter((item) => !isOrderItemAddable(item))];
}

export function orderItemsCountLabel(count, t) {
  const value = Math.max(0, Number(count || 0));
  return value === 1 ? t("ordersUi.oneItem") : fill(t("ordersUi.manyItems"), { count: value });
}

export function hasActiveOrderFilters({ search, dateFrom, dateTo, source }) {
  return Boolean(String(search || "").trim() || dateFrom || dateTo || source);
}

export function resolveOrderListRange({ page, pageSize, shown, total }) {
  if (!shown || !total) {
    return { from: 0, to: 0 };
  }

  const from = (Math.max(1, page) - 1) * pageSize + 1;
  return { from, to: Math.min(from + shown - 1, total) };
}

/** "044 123 456 · Prishtinë · 3 porosi" for a recipient suggestion. */
export function formatCustomerSuggestionMeta(customer, t) {
  const total = Number(customer?.orderStats?.total || 0);

  return [
    customer?.phone || null,
    customer?.city || null,
    total ? fill(t("ordersWizard.customerOrders"), { count: total }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Scan and manual search share one feedback slot. Starting to type in either box
 * means the old barcode message is stale, so it is cleared.
 */
export function feedbackAfterFinderInput(feedback, nextValue) {
  return String(nextValue || "").length ? null : feedback;
}
